<script>
import { mapGetters } from 'vuex';
import Avatar from 'dashboard/components/widgets/Avatar.vue';

// Chatsell: el buscador tiene que matchear "optica" con "óptica", así que
// normalizamos acentos antes de comparar.
const normalize = value =>
  (value || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

// Debajo de este número la lista entra completa en pantalla y el buscador
// sería solo ruido.
const MIN_ACCOUNTS_FOR_SEARCH = 5;

export default {
  components: {
    Avatar,
  },
  props: {
    showAccountModal: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      query: '',
      activeIndex: 0,
    };
  },

  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
    }),
    accounts() {
      return this.currentUser.accounts || [];
    },
    showSearch() {
      return this.accounts.length >= MIN_ACCOUNTS_FOR_SEARCH;
    },
    filteredAccounts() {
      const term = normalize(this.query).trim();
      if (!term) return this.accounts;

      return this.accounts.filter(account =>
        `${normalize(account.name)} ${normalize(
          this.roleName(account)
        )}`.includes(term)
      );
    },
  },

  watch: {
    query() {
      this.activeIndex = 0;
    },
    showAccountModal(isOpen) {
      if (isOpen) this.resetSelector();
    },
  },

  mounted() {
    if (this.showAccountModal) this.resetSelector();
  },

  methods: {
    roleName(account) {
      return account.custom_role_id ? account.custom_role.name : account.role;
    },
    resetSelector() {
      this.query = '';
      this.activeIndex = 0;
      this.$nextTick(() => {
        if (this.$refs.searchInput) this.$refs.searchInput.focus();
      });
    },
    moveActiveIndex(offset) {
      const total = this.filteredAccounts.length;
      if (!total) return;

      this.activeIndex = (this.activeIndex + offset + total) % total;
      this.$nextTick(() => {
        const rows = this.$refs.accountRows;
        const row = rows && rows[this.activeIndex];
        if (row) row.scrollIntoView({ block: 'nearest' });
      });
    },
    onEnter() {
      const account = this.filteredAccounts[this.activeIndex];
      if (account) this.onChangeAccount(account.id);
    },
    onChangeAccount(accountId) {
      const accountUrl = `/app/accounts/${accountId}/dashboard`;
      window.location.href = accountUrl;
    },
  },
};
</script>

<template>
  <woot-modal
    :show="showAccountModal"
    :on-close="() => $emit('closeAccountModal')"
  >
    <woot-modal-header
      :header-title="$t('SIDEBAR_ITEMS.CHANGE_ACCOUNTS')"
      :header-content="$t('SIDEBAR_ITEMS.SELECTOR_SUBTITLE')"
    />

    <div v-if="showSearch" class="px-8 pt-4">
      <div class="relative">
        <fluent-icon
          icon="search"
          size="16"
          class="absolute -translate-y-1/2 pointer-events-none top-1/2 text-slate-400 dark:text-slate-500 ltr:left-3 rtl:right-3"
        />
        <input
          ref="searchInput"
          v-model="query"
          type="text"
          autocomplete="off"
          class="w-full h-10 py-0 m-0 text-sm border rounded-lg ltr:pl-9 ltr:pr-9 rtl:pr-9 rtl:pl-9 bg-slate-25 dark:bg-slate-900 border-slate-100 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:border-woot-500 dark:focus:border-woot-500"
          :placeholder="$t('SIDEBAR_ITEMS.SEARCH_ACCOUNTS')"
          @keydown.down.prevent="moveActiveIndex(1)"
          @keydown.up.prevent="moveActiveIndex(-1)"
          @keydown.enter.prevent="onEnter"
        />
        <button
          v-if="query"
          type="button"
          class="absolute flex items-center justify-center -translate-y-1/2 rounded top-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 ltr:right-3 rtl:left-3"
          :aria-label="$t('SIDEBAR_ITEMS.CLEAR_SEARCH')"
          @click="resetSelector"
        >
          <fluent-icon icon="dismiss" size="16" />
        </button>
      </div>
    </div>

    <div
      class="flex flex-col gap-1 px-8 py-4 overflow-y-auto max-h-[45vh]"
      role="listbox"
    >
      <button
        v-for="(account, index) in filteredAccounts"
        :id="`account-${account.id}`"
        :key="account.id"
        ref="accountRows"
        role="option"
        :aria-selected="account.id === accountId ? 'true' : 'false'"
        class="flex items-center w-full gap-3 px-3 py-2 text-left transition-colors border rounded-lg cursor-pointer rtl:text-right"
        :class="
          account.id === accountId
            ? 'bg-woot-25 dark:bg-woot-900/30 border-woot-200 dark:border-woot-700'
            : index === activeIndex
            ? 'bg-slate-50 dark:bg-slate-700/50 border-transparent'
            : 'border-transparent hover:bg-slate-50 dark:hover:bg-slate-700/50'
        "
        @click="onChangeAccount(account.id)"
        @mouseenter="activeIndex = index"
      >
        <Avatar
          :username="account.name"
          :size="36"
          class="flex-shrink-0 rounded-full w-9 h-9"
        />
        <span class="flex flex-col min-w-0 grow">
          <span
            class="text-sm font-medium leading-5 truncate text-slate-800 dark:text-slate-50"
          >
            {{ account.name }}
          </span>
          <span
            class="text-xs leading-4 capitalize truncate text-slate-500 dark:text-slate-400"
          >
            {{ roleName(account) }}
          </span>
        </span>
        <span
          v-if="account.id === accountId"
          class="flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full text-woot-600 dark:text-woot-400 bg-woot-50 dark:bg-woot-900/50"
        >
          {{ $t('SIDEBAR_ITEMS.CURRENT_ACCOUNT') }}
        </span>
      </button>

      <div
        v-if="!filteredAccounts.length"
        class="flex flex-col items-center gap-2 py-10 text-center"
      >
        <fluent-icon
          icon="search"
          size="24"
          class="text-slate-300 dark:text-slate-600"
        />
        <p class="mb-0 text-sm text-slate-500 dark:text-slate-400">
          {{ $t('SIDEBAR_ITEMS.NO_ACCOUNTS_FOUND', { query }) }}
        </p>
      </div>
    </div>

    <div
      v-if="globalConfig.createNewAccountFromDashboard"
      class="flex items-center justify-end gap-2 px-8 pt-4 pb-8"
    >
      <button
        class="w-full button success large expanded nice"
        @click="$emit('showCreateAccountModal')"
      >
        {{ $t('CREATE_ACCOUNT.NEW_ACCOUNT') }}
      </button>
    </div>
  </woot-modal>
</template>
